import { AmountType, TransactionType } from '../../../shared/types/Transaction';
import { CheckAbbreviatedRecipientTypes, frequencyDict, transactionTypes } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Control } from './Control';
import { ComponentState } from '../../../shared/types/ComponentState';
import { DistributionReason } from '../internal-transfer/DistributionReason';
import { ContributionReason } from '../internal-transfer/ContributionReason';
import { ErrorDetail } from '../shared/ServiceErrorDetails';
import { CodeAndDescription } from '../shared/CodeAndDescription';
import { ItCloseAccount, ItTransferType } from './FormControlTypes';
import { Identifier } from '../wire/Identifier';
import { TaxRemittance } from '../wire/WireInfo';

export class TransactionForm extends ComponentState {
  transactionType: TransactionType | '';
  valid?: boolean;
  extraPrompts?: ExtraPrompts;
  transactionDetails: TransactionDetails;
  showCashieringIRAAgreement: boolean;
  serviceError: TransactionFormServiceError;
  stateList?: CodeAndDescription[];
  countryList?: CodeAndDescription[];
  prefilledFlow: {
    state: prefilledFlowState,
    name: prefilledFlowName
  };
  createTransactionInProgress: boolean;
  continueClickTotalDistTriggered?: boolean;
  acknowledgeCallV3FailedForToggleOn?: boolean;
  acknowledgeCallV3Failed?: boolean;
}

export type ExtraPrompts = AchExtraPrompts | ItExtraPrompts | ChecksExtraPrompts | WireExtraPrompts;
export type TransactionDetails = ItTransactionDetails | AchTransactionDetails | CheckTransactionDetails | WireTransactionDetails;

export type prefilledFlowState = 'started' | 'inProgress' | 'complete' | '';
export type prefilledFlowName = 'edit-review' | 'edit-table' | '';

export const prefilledFlowNames: { [key: string]: prefilledFlowName } = {
  editTable: 'edit-table',
  editReview: 'edit-review',
};

export const prefilledFlowStates: { [key: string]: prefilledFlowState } = {
  started: 'started',
  progress: 'inProgress',
  complete: 'complete'
};

export interface AchForm extends TransactionForm {
  transactionType: 'ach';
  extraPrompts: AchExtraPrompts;
  transactionDetails: AchTransactionDetails;
}

export interface ItForm extends TransactionForm {
  transactionType: 'internal-transfer';
  extraPrompts: ItExtraPrompts;
  transactionDetails: ItTransactionDetails;
}

export interface WireForm extends TransactionForm {
  transactionType: 'wire',
  extraPrompts: WireExtraPrompts;
  transactionDetails: WireTransactionDetails;
}

export interface CheckForm extends TransactionForm {
  transactionType: 'check',
  extraPrompts: ChecksExtraPrompts;
  transactionDetails: CheckTransactionDetails;
}

export interface TransactionFormControls {
  transactionType: Control;
  direction: Control;
  frequency: Control;
  useStandingAuth: Control;
}
export interface AchExtraPrompts {
  transferDirection: 'incoming' | 'outgoing' | '';
  standingAuthId: string; // first letter of transaction type + cashieringRequestId + cashieringRequestSecondId
  requestingAccountHolderId?: string;
  frequency?: keyof typeof frequencyDict;
}

export interface ItExtraPrompts {
  useStandingAuth: 'yes' | 'no' | 'bundled' | '';
  useBundledStandingAuth?: 'yes' | 'no' | 'recurring' | '';
  frequency?: keyof typeof frequencyDict;
  standingAuthId?: string;
  transferType?: ItTransferType;
  closeAccount?: ItCloseAccount;
  taxForms?: boolean;
  accountStatements?: boolean;
  tradeConfirmations?: boolean;
  taxFormsDateInputControl?: string;
  accountStatementsDateInputControl?: string;
  tradeConfirmationsDateInputControl?: string;
}

export interface ChecksExtraPrompts {
  useStandingAuth: 'yes' | 'no' | 'bundled' | '';
  useBundledStandingAuth?: 'yes' | 'no' | 'recurring' | 'onetimeTotalDist' |'';
  frequency?: keyof typeof frequencyDict;
  standingAuthId?: string;
  closeAccount?: 'yes' | 'no' | '';
  taxForms?: boolean;
  accountStatements?: boolean;
  tradeConfirmations?: boolean;
  taxFormsDateInputControl?: string;
  accountStatementsDateInputControl?: string;
  tradeConfirmationsDateInputControl?: string;
}

export interface WireExtraPrompts {
  useStandingAuth: 'yes' | 'no' | 'bundled' | '';
  useBundledStandingAuth?: 'yes' | 'no' | '';
  useTemplates?: 'yes'|'no'|'';
  standingAuthId?: string;
  requestingAccountHolderId?: string;
  templateId?: string;
}

export interface ItToAccountDetails {
  toAccount: string;
  iraDistributionType: string;
  iraContributionType: string;
  trustDepositCode?: string;
  trustIncomeToPercent?: string;
  trustPrincipalToPercent?: string;
}

export interface ItCashAmounts {
  toAccount: string;
  amount: string;
  amountType: string;
}

export interface ItTransactionDetails {
  toAccount: string;
  processDate: string;
  amount: string;
  amountType: AmountType;
  taxWithholdingsFormGroup: TaxWithholdingFields;
  recurringFieldsFormGroup: RecurringFields;
  iraContributionType: string;
  iraContributionTypeMap: ContributionReason[];
  trustIncomePercent?: string;
  trustPrincipalPercent?: string;
  trustIncomeToPercent?: string;
  trustPrincipalToPercent?: string;
  trustDepositCode: string;
  trustTaxCategory?: string;
  trustTaxCode?: string;
  trustPaidFor?: string;
  trustComment?: string;
  costBasisMethod?: string
  toAccounts?: ItToAccountDetails[];
  cashAmounts?: ItCashAmounts[];
}



export interface RecurringFields {
  recurringFrequency: string;
  endDate: string;
  secondProcessDate: string;
  payIncome: string;
}

export interface AchTransactionDetails extends AchTransactionDetailsFromInputCall {
  withdrawalDate: string;
  completionDate: string;
  fundsAvailableToTrade: string;
  settlementDate: string;
  amount: string;
  amountType: AmountType;
  transferAmount: string;
  contributionReason: string;
  selectedAuthorization: string;
  transferDirection: string;
  trustIncomePercent?: string;
  trustPrincipalPercent?: string;
  trustTaxCategory?: string;
  trustTaxCode?: string;
  trustPaidFor?: string;
  trustComment?: string;
  costBasisMethod?: string
  endDate?: string;
  secondProcessDate?: string;
  recurringFrequency?: string;
}

export interface AchTransactionDetailsFromInputCall {
  taxWithholdingsFormGroup?: TaxWithholdingFields;
}

export interface CheckTransactionDetails {
  payeeType?: 'firstParty' | 'thirdParty';
  payeeName?: string;
  modifiedPayeeName?: string;
  defaultPayee?: string[];
  additionalPayeeName1?: string;
  additionalPayeeName2?: string;
  addresseeName?: string;
  addresseeNameCont?: string;
  addresseeNameCont2?: string;
  streetAddress?: string;
  streetAddressCont?: string;
  frequency?: string;
  frequencyDescription?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  recipient?: CheckAbbreviatedRecipientTypes;
  processDate?: string;
  checkReason?: string;
  checkMemo?: string;
  memoLine2?: string;
  amount?: string;
  payIncome?: string;
  recurringFrequency?: string;
  secondProcessDate?: string;
  endDate?: string;
  nextProcessDate?: string;
  amountType?: string;
  deliveryMethod?: string;
  trustIncomePercent?: string;
  trustPrincipalPercent?: string;
  trustTaxCategory?: string;
  trustTaxCode?: string;
  trustPaidFor?: string;
  trustComment?: string;
  costBasisMethod?: string
  taxWithholdingsFormGroup?: TaxWithholdingFields;
  distributionReasonCode?: string;
  distributionReasonSubCode?: string;
  modifiedPayeeCheckbox?: boolean;
  modifiedPayeeName1?: string;
  modifiedPayeeName2?: string;
  modifiedPayeeName3?: string;
}

export interface WireTransactionDetails extends receivingbankDetails, receivingAccountHolder, receivingAccountHolderIso {
  aba: string;
  recipient: 'firstParty' | 'thirdParty' | '';
  toAccount: string;
  selectedDate: string;
  amount: string;
  isEscrow: boolean;
  remittanceInformation: string;
  transmissionNotes: string;
  trustIncomePercent?: string;
  trustPrincipalPercent?: string;
  trustTaxCategory?: string;
  trustTaxCode?: string;
  trustPaidFor?: string;
  trustComment?: string;
  taxWithholdingsFormGroup?: TaxWithholdingFields;
  amountType: string;
  intermediarySelection: 'yes' | 'no' | '';
  intermediaryName: string;
  intermediaryNameCont: string;
  intermediaryAccountNumber: string;
  intermediaryAddress: string;
  intermediaryAddressCont: string;
  intermediaryCountry: string;
  intermediaryPhone: string;
  canDisplayIntermediaryPhone: boolean;
  creditToSelection: 'yes' | 'no' | '';
  creditToName: string;
  creditToNameCont: string;
  creditToAccountNumber: string;
  creditToAddress: string;
  creditToAddressCont: string;
  creditToCountry: string;
  creditToPhone: string;
  canDisplayCreditToPhone: boolean;
  costBasisMethod?: string;
  labelRefNum: string;
  refNum: string;
  requestedDate: string;
  payeeName?: string;
  modifiedPayeeCheckbox?: boolean;
  modifiedPayeeName1?: string;
  modifiedPayeeName2?: string;
  isoModifiedPayeeName?: string;

  // v3 properties
  isISOCompliant: boolean;

  receivingAccountHolderName: string;
  receivingAccountHolderStreetName: string;
  receivingAccountHolderStreetNumber: string;
  receivingAccountHolderApartmentSuiteEtc: string;
  receivingAccountHolderCity: string;
  receivingAccountHolderState: string;
  receivingBankCountry: string;
  receivingAccountHolderZip: string;
  receivingAccountHolderDepartment: string;
  receivingAccountHolderBuildingName: string;
  receivingAccountHolderFloor: string;
  receivingAccountHolderTownLocationName: string;
  receivingAccountHolderDistrict: string;
  receivingAccountHolderPhone: string;

  intermediaryStreetNumber: string;
  intermediaryStreetName: string;
  intermediaryApartmentSuiteEtc: string;
  intermediaryCity: string;
  intermediaryState: string;
  intermediaryZip: string;
  intermediaryDepartment: string;
  intermediaryBuildingName: string;
  intermediaryFloor: string;
  intermediaryTownLocationName: string;
  intermediaryDistrict: string;
  intermediaryConcatenatedAddress: string;
  intermediaryIdentifierList: Identifier[];
  intermediaryIBAN?: string | null;
  intermediaryBIC: string;
  intermediaryClearingSysId: string;

  secondIntermediaryIdentifierList: Identifier[];
  secondIntermediaryName: string;
  secondIntermediaryConcatenatedAddress: string;
  secondIntermediaryCountry: string;
  secondIntermediaryAccountNumber: string;
  secondIntermediaryIBAN: string;
  secondIntermediaryBIC: string;
  secondIntermediaryClearingSysId: string;

  secondIntermediaryStreetNumber: string;
  secondIntermediaryStreetName: string;
  secondIntermediaryApartmentSuiteEtc: string;
  secondIntermediaryCity: string;
  secondIntermediaryState: string;
  secondIntermediaryZip: string;
  secondIntermediaryDepartment: string;
  secondIntermediaryBuildingName: string;
  secondIntermediaryFloor: string;
  secondIntermediaryTownLocationName: string;
  secondIntermediaryDistrict: string;

  isThreeStep: boolean;
  isFourStep: boolean;
  thirdIntermediaryIdentifierList: Identifier[];
  thirdIntermediaryName: string;
  thirdIntermediaryConcatenatedAddress: string;
  thirdIntermediaryCountry: string;
  thirdIntermediaryAccountNumber: string;
  thirdIntermediaryIBAN: string;
  thirdIntermediaryBIC: string;
  thirdIntermediaryClearingSysId: string;

  thirdIntermediaryStreetNumber: string;
  thirdIntermediaryStreetName: string;
  thirdIntermediaryApartmentSuiteEtc: string;
  thirdIntermediaryCity: string;
  thirdIntermediaryState: string;
  thirdIntermediaryZip: string;
  thirdIntermediaryDepartment: string;
  thirdIntermediaryBuildingName: string;
  thirdIntermediaryFloor: string;
  thirdIntermediaryTownLocationName: string;
  thirdIntermediaryDistrict: string;

  taxPayerId: string;
  taxTypeCode: string;
  taxMonth: string;
  taxYear: string;
}

interface receivingbankDetails {
  receivingBankName1: string;
  receivingBankName2: string;
  receivingBankAddress1: string;
  receivingBankAddress2: string;
  receivingBankCountry: string;
  bankPhoneNumber: string;
  canDisplayReceivingBankPhone: boolean;
  bankID: string;
  receivingBankName: string;
  receivingBankIdentifierList: Identifier[],
  receivingBankClearingSysId: string;
  receivingBankConcatenatedAddress: string;

  receivingBankStreetNumber: string;
  receivingBankStreetName: string;
  receivingBankApartmentSuiteEtc: string;
  receivingBankCity: string;
  receivingBankState: string;
  receivingBankZip: string;
  receivingBankDepartment: string;
  receivingBankBuildingName: string;
  receivingBankFloor: string;
  receivingBankTownLocationName: string;
  receivingBankDistrict: string;
}

interface receivingAccountHolder {
  receivingAccountHolderName1: string;
  receivingAccountHolderName2: string;
  receivingAccountHolderAddress1: string;
  receivingAccountHolderAddress2: string;
  receivingAccountHolderCountry: string;
}

interface receivingAccountHolderIso {
  name: string;
  receivingAccountHolderName: string;
  receivingAccountHolderAccountNumber: string;
  receivingAccountHolderStreetName: string;
  receivingAccountHolderStreetNumber: string;
  receivingAccountHolderCountry: string;
  receivingAccountHolderApartmentSuiteEtc: string;
  receivingAccountHolderCity: string;
  receivingAccountHolderState: string;
  receivingAccountHolderZip: string;
  receivingAccountHolderDepartment: string;
  receivingAccountHolderBuildingName: string;
  receivingAccountHolderFloor: string;
  receivingAccountHolderTownLocationName: string;
  receivingAccountHolderDistrict: string;
  receivingAccountHolderConcatenatedAddress: string;
  receivingAccountHolderIdentifierList: Identifier[];
  // country codes have 2 characters
  countryCode: string;
  countryDescription?: string;
}

export interface TaxWithholdingFields {
  fedTaxPercent: string;
  fedTaxAmount: string;
  fedTaxAmountFormatted?: string;
  stateTaxPercent: string;
  stateTaxAmount: string;
  stateTaxAmountFormatted?: string;
  distributionReason: string;
  stateHasTaxes?: boolean;
  distributionReasonMap?: DistributionReason[];
  stateTypeIndicator?: string;
  stateCode?: string;
  defaultStateTax?: string;
  defaultFedTax?: string;
  fedTaxRate?: number;
  stateTaxRate?: number;
}

export const isAchForm = (form: TransactionForm): form is AchForm => {
  return form.transactionType === transactionTypes.ach;
}

export const isAchExtraPrompts = (prompts: ExtraPrompts | undefined): prompts is AchExtraPrompts => {
  if (prompts && 'transferDirection' in prompts) {
    return true;
  }
  return false
}

export const isAchTransactionDetails = (details: TransactionDetails | undefined): details is AchTransactionDetails => {
  if (details && 'completionDate' in details) {
    return true;
  }
  return false;
}

export const isInternalTransferForm = (form: TransactionForm): form is ItForm => {
  return form.transactionType === transactionTypes.internalTransfer;
}

export const isItExtraPrompts = (prompts: ExtraPrompts | undefined): prompts is ItExtraPrompts => {
  if (prompts && 'useStandingAuth' in prompts) {
    return true;
  }
  return false
}

export const isItTransactionDetails = (details: TransactionDetails): details is ItTransactionDetails => {
  if (details && 'toAccount' in details) {
    return true;
  }
  return false;
}

export const isWireTransactionDetails = (details: TransactionDetails): details is WireTransactionDetails => {
  if (details && 'recipient' in details) {
    return true;
  }
  return false;
}

export const isWireExtraPrompts = (prompts: ExtraPrompts): prompts is WireExtraPrompts => {
  if (prompts && !('transferType' in prompts) && !('transferDirection' in prompts)) {
    return true;
  }
  return false
}

export const isCheckExtraPrompts = (prompts: ExtraPrompts): prompts is ChecksExtraPrompts => {
  if (prompts && 'useStandingAuth' in prompts && !('transferType' in prompts) && !('transferDirection' in prompts)) {
    return true;
  }
  return false
}

export const isCheckTransactionDetails = (details: TransactionDetails): details is CheckTransactionDetails => {
  if (details && 'payeeType' in details) {
    return true;
  }
  return false;
}

export const isCheckTransactionForm = (form: TransactionForm): form is CheckForm => {
  return form.transactionType === transactionTypes.check;
}

export const isWireForm = (form: TransactionForm): form is WireForm => {
  return form.transactionType === transactionTypes.wire;
}

export interface TransactionFormServiceError {
  status: number;
  url: string;
  name: string;
  message: string;
  correlationId: string;
  errorList?: ErrorDetail[];
  hasError: boolean;
}

export type TrustControls = {
  trustIncomePercent?: string;
  trustPrincipalPercent?: string;
  trustTaxCategory?: string;
  trustTaxCode?: string;
  trustPaidFor?: string;
  trustComment?: string;
}
